import { productSlug } from './paths';
import { type CatalogSearchResult, searchCatalog } from './search';
import type { Catalog, Category, CategoryPage, LocaleCode, Product, SubFilter, SubcategoryPage } from './types';
import { isProductParent, resolveInheritedProduct } from './variants';
import { config } from '~/config/config';
import catalogJson from '~/data/catalog.json';

const localCatalog = catalogJson as Catalog;

let cached: Catalog | null = null;

function withResolvedProducts(catalog: Catalog): Catalog {
  const byId = new Map(catalog.products.map((p) => [p.id, p]));

  return {
    ...catalog,
    products: catalog.products.map((p) => resolveInheritedProduct(p, byId)),
    categories: catalog.categories.map((c) => ({
      ...c,
      slug: c.slug || c.id,
    })),
    subcategoryPages: catalog.subcategoryPages.map((s) => ({
      ...s,
      slug: s.slug || s.id,
    })),
  };
}

export async function getCatalog(): Promise<Catalog> {
  if (cached) {
    return cached;
  }

  if (config.catalogUrl) {
    const response = await fetch(config.catalogUrl);
    if (!response.ok) {
      throw new Error(`Failed to load catalog (${response.status})`);
    }
    cached = withResolvedProducts((await response.json()) as Catalog);

    return cached;
  }

  cached = withResolvedProducts(localCatalog);

  return cached;
}

export function getCatalogSync(): Catalog {
  return cached ?? withResolvedProducts(localCatalog);
}

/** Clear in-memory catalog cache (tests / hot reload helpers). */
export function clearCatalogCache(): void {
  cached = null;
}

export async function getProducts(): Promise<Product[]> {
  return (await getCatalog()).products;
}

/** The product a URL segment points to: its slug, or its id or code in any case. */
export function findProduct(products: Product[], idOrSlug: string): Product | undefined {
  const needle = idOrSlug.toLowerCase();

  return products.find(
    (product) =>
      productSlug(product.id) === needle ||
      product.id.toLowerCase() === needle ||
      product.code.toLowerCase() === needle,
  );
}

export async function getProduct(id: string): Promise<Product | undefined> {
  return findProduct(await getProducts(), id);
}

export async function getCategories(): Promise<Category[]> {
  return (await getCatalog()).categories;
}

export async function getCategoryBySlugOrId(slugOrId: string): Promise<Category | undefined> {
  const needle = slugOrId.toLowerCase();

  // `legacySlugs` keeps URLs from before a rename resolving; the routes then redirect them to `slug`.
  return (await getCategories()).find(
    (c) =>
      c.slug.toLowerCase() === needle ||
      c.id.toLowerCase() === needle ||
      (c.legacySlugs ?? []).some((legacy) => legacy.toLowerCase() === needle),
  );
}

export async function getCategoryPage(id: string): Promise<CategoryPage | undefined> {
  const category = await getCategoryBySlugOrId(id);
  const pageId = category?.id ?? id;

  return (await getCatalog()).categoryPages.find((page) => page.id === pageId);
}

export async function getSubcategoryPage(id: string): Promise<SubcategoryPage | undefined> {
  const needle = id.toLowerCase();

  return (await getCatalog()).subcategoryPages.find(
    (page) => page.id.toLowerCase() === needle || page.slug.toLowerCase() === needle,
  );
}

/**
 * Resolve subcategory by English slug or legacy id, scoped to a category (slug or id).
 */
export async function getSubcategoryBySlugOrId(
  categorySlugOrId: string,
  subcategorySlugOrId: string,
): Promise<SubcategoryPage | undefined> {
  const catalog = await getCatalog();
  const category = await getCategoryBySlugOrId(categorySlugOrId);
  const categoryId = category?.id;
  const needle = subcategorySlugOrId.toLowerCase();

  const candidates = catalog.subcategoryPages.filter((page) => {
    if (categoryId && page.categoryId !== categoryId) {
      return false;
    }

    return page.id.toLowerCase() === needle || page.slug.toLowerCase() === needle;
  });

  if (candidates.length === 1) {
    return candidates[0];
  }
  if (candidates.length > 1 && categoryId) {
    return candidates.find((p) => p.categoryId === categoryId);
  }

  // Fallback: legacy id without category match
  return catalog.subcategoryPages.find(
    (page) => page.id.toLowerCase() === needle || (page.slug.toLowerCase() === needle && !categoryId),
  );
}

export function productsBySubcategory(products: Product[], subcategoryId: string): Product[] {
  return products.filter((product) => product.subcategoryIds.includes(subcategoryId));
}

export function subcategoryProductCount(products: Product[], subcategoryId: string): number {
  return productsBySubcategory(products, subcategoryId).filter(isProductParent).length;
}

export function toFilterArray(val: string | string[] | undefined): string[] {
  if (!val) {
    return [];
  }

  return Array.isArray(val) ? val : [val];
}

/**
 * The sub-filter tiles worth showing: one whose label no product carries leads to an empty list,
 * so it is dropped rather than offered as a dead end.
 */
export function visibleSubFilters(
  subFilters: SubFilter[] | undefined,
  products: Product[],
  subcategoryId: string,
): SubFilter[] {
  const inSubcategory = productsBySubcategory(products, subcategoryId);

  return (subFilters ?? []).filter((subFilter) =>
    inSubcategory.some((product) => toFilterArray(product.filterLabels?.[subcategoryId]).includes(subFilter.label)),
  );
}

export async function searchProducts(
  query: string,
  locale: LocaleCode,
  t: (key: string) => string,
): Promise<CatalogSearchResult> {
  return searchCatalog(await getCatalog(), query, locale, t);
}
